import { useMemo } from "react";
import type { ComponentType, ReactNode } from "react";

import {
  CheckIcon,
  EyeIcon,
  NotePencilIcon,
  PencilSimpleIcon,
  RocketLaunchIcon,
  StackletLogo,
  UserCircleGearIcon,
  UserIcon,
  XIcon,
  type IconWithSizeProps,
} from "@stacklet/ui";
import { graphql, useFragment } from "react-relay";

import { ROLES } from "app/constants/rbac";

import { RbacChip, typenameChipMap } from "./RbacChip";

import type {
  RoleAssignmentTable_principal$data,
  RoleAssignmentTable_principal$key,
} from "./__generated__/RoleAssignmentTable_principal.graphql";

/** A role assignment target, as returned by the fragment (a `__typename`-discriminated union) */
type Target = NonNullable<
  RoleAssignmentTable_principal$data["roleAssignments"]["edges"]
>[number]["node"]["target"];

/** Maps each RBAC role to its leading icon in the scope column */
const ROLE_ICONS: Record<string, ComponentType<IconWithSizeProps>> = {
  "stacklet-user": UserIcon,
  viewer: EyeIcon,
  "resource-viewer": EyeIcon,
  creator: NotePencilIcon,
  editor: PencilSimpleIcon,
  "policy-deployer": RocketLaunchIcon,
  owner: UserCircleGearIcon,
  admin: StackletLogo,
};

/** Turns a kebab-case role name into a label, e.g. "resource-viewer" -> "Resource Viewer" */
function roleLabel(name: string): string {
  return name
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function targetName(target: Target): string {
  if ("displayName" in target) return target.displayName;
  if ("name" in target) return target.name;
  return "";
}

/** Renders the leading role icon followed by the role's scope (chips, or a granted/not-granted status) */
function ScopeCell({
  role,
  targets,
}: {
  /** Role name, used to resolve the leading icon */
  role: string;
  /** Targets assigned to this role */
  targets: Target[];
}) {
  const RoleIcon = ROLE_ICONS[role];

  // A `RoleScope` target means the role is granted at the system level rather
  // than scoped to specific resources.
  const resourceTargets = targets.filter((t) => t.__typename !== "RoleScope");

  let content: ReactNode;
  if (targets.length === 0) {
    content = (
      <XIcon
        aria-label="Not assigned"
        className="size-3.5 shrink-0 text-text-negative"
      />
    );
  } else if (resourceTargets.length === 0) {
    // Purely system-scoped: show a granted status rather than a chip.
    content = (
      <CheckIcon
        aria-label="Granted"
        className="size-3.5 shrink-0 text-text-positive"
      />
    );
  } else {
    // Resource-scoped, possibly alongside a system-level grant. Render every
    // target as a chip so a mixed `RoleScope` shows as a System chip rather
    // than being dropped.
    content = targets.map((target, i) => {
      const rbacType = typenameChipMap[target.__typename];
      if (!rbacType) return null;

      return (
        <RbacChip
          key={`${target.__typename}:${targetName(target)}-${i}`}
          label={targetName(target)}
          rbacType={rbacType}
          variant="scope"
        />
      );
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-xs">
      {RoleIcon ? (
        <RoleIcon className="size-3.5 shrink-0 text-foreground-primary" />
      ) : null}
      {content}
    </div>
  );
}

/** Props for the RoleAssignmentTable component */
interface Props {
  /** A `User` or `UserGroup` (anything implementing `RolePrincipal`) */
  principalRef: RoleAssignmentTable_principal$key;
  /** Whether to also render roles that have no assignments. Defaults to `false` */
  showEmptyRoles?: boolean;
  /**
   * Whether to drop assignments owned by an identity-provider sync (those with
   * a non-null `source`), so only direct, user-made assignments are shown.
   * Defaults to `false`. Set when a separate indirect-roles section renders the
   * synced assignments, to avoid double-counting.
   */
  excludeSynced?: boolean;
}

/**
 * Displays a principal's (user or group) role assignments: one row per role,
 * with a leading role icon and the scope of each role shown as chips.
 */
export function RoleAssignmentTable({
  principalRef,
  showEmptyRoles = false,
  excludeSynced = false,
}: Props) {
  const principal = useFragment(
    graphql`
      fragment RoleAssignmentTable_principal on RolePrincipal {
        roleAssignments(first: 100) {
          edges {
            node {
              source {
                __typename
              }
              role {
                name
              }
              target {
                __typename
                ... on RoleScope {
                  displayName
                }
                ... on AccountGroup {
                  name
                }
                ... on PolicyCollection {
                  name
                }
                ... on RepositoryConfig {
                  name
                }
              }
            }
          }
        }
      }
    `,
    principalRef,
  );

  // Group targets by role name
  const roleMap = useMemo(() => {
    const map: Record<string, Target[]> = {};
    for (const edge of principal.roleAssignments?.edges ?? []) {
      const node = edge?.node;
      if (!node) continue;
      if (excludeSynced && node.source) continue;

      (map[node.role.name] ??= []).push(node.target);
    }
    return map;
  }, [principal, excludeSynced]);

  // Order rows by the canonical role list, appending any unknown roles, and
  // drop empty roles unless the caller opts in.
  const rows = useMemo(() => {
    const ordered = [
      ...new Set([...Object.keys(ROLES), ...Object.keys(roleMap)]),
    ];
    return ordered
      .filter((role) => showEmptyRoles || (roleMap[role]?.length ?? 0) > 0)
      .map((role) => ({ role, targets: roleMap[role] ?? [] }));
  }, [roleMap, showEmptyRoles]);

  if (rows.length === 0) {
    return (
      <p className="py-sm text-label-medium text-text-secondary">
        No role assignments.
      </p>
    );
  }

  return (
    <table className="w-full border-collapse text-label-medium text-text-primary">
      <thead>
        <tr>
          <th className="w-[8.25rem] whitespace-nowrap border-r border-overlay-medium px-md py-sm text-right align-middle font-normal">
            Role
          </th>
          <th className="px-md py-sm text-left align-middle font-normal">
            Scope
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ role, targets }) => (
          <tr className="border-t border-overlay-medium" key={role}>
            <td className="w-[8.25rem] whitespace-nowrap border-r border-overlay-medium px-md py-sm text-right align-middle">
              {roleLabel(role)}
            </td>
            <td className="px-md py-sm align-middle">
              <ScopeCell role={role} targets={targets} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
